import React from 'react';
import { components as selectComponents } from 'react-select';
import type { GroupBase, OptionProps } from 'react-select';
import { AttributeWidget, Tooltip } from '@usealloy/component-library';
import type { Attribute } from 'flow/store/engine/types';

type AttributeOptionData = {
  label: string;
  value: string | null;
  meta?: {
    attribute?: Attribute;
    showAttributeWidget?: boolean;
  };
};

const WIDGET_TRIGGER_STYLE = {
  display: 'block',
  width: '100%',
  whiteSpace: 'nowrap' as const,
};

/** Neutralises Tooltip's own card chrome — AttributeWidget brings its own. */
const WIDGET_TOOLTIP_STYLE = {
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  maxWidth: 'none',
  padding: 0,
};

/**
 * Custom react-select `Option` renderer used by attribute dropdown to show the attribute widget
 */
export const AttributeWidgetOption = (
  // `unknown` matches how the component library types MultiSelect's `components` prop
  optionProps: OptionProps<unknown, boolean, GroupBase<unknown>>,
) => {
  const {
    children,
    data,
    isSelected,
    ...restProps
  } = optionProps;

  const { isFocused } = restProps;

  const { label, value, meta } = data as AttributeOptionData;

  const shouldShowWidget = Boolean(meta?.showAttributeWidget) && isFocused;

  // isSelected passed in to prevent default styling
  return (
    <selectComponents.Option {...restProps} data={data} isSelected={false}>
      <Tooltip
        align="start"
        side="right"
        open={shouldShowWidget}
        hide={!shouldShowWidget}
        style={WIDGET_TOOLTIP_STYLE}
        content={<AttributeWidget label={label} description={meta?.attribute?.description} />}
      >
        <span
          role="option"
          aria-selected={isSelected}
          data-testid={`option-container-${value}`}
          style={WIDGET_TRIGGER_STYLE}
        >
          {children}
        </span>
      </Tooltip>
    </selectComponents.Option>
  );
};
